import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Select, Button, Space, Typography, Tag, message, Input, Popconfirm, Empty, Tooltip, Tabs, Card, Badge, Divider, Segmented } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
    SaveOutlined,
    PlayCircleOutlined,
    SearchOutlined,
    CloseCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { 
    Sparkles, 
    Globe, 
    User, 
    ShieldCheck, 
    Activity, 
    TestTube, 
    Command, 
    MousePointerClick,
    Plus,
    Search,
    Trash2,
    Play,
    Settings2,
    CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Method_POST_CONTEXTO } from '../../../../../service/api';

const { Text, Title } = Typography;

const cardStyle = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(31, 38, 135, 0.07)',
    position: 'relative',
    overflow: 'hidden',
    flex: 1,
    transition: 'all 0.3s ease'
};

const inputStyle = {
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    padding: '10px 16px',
    fontSize: '14px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    width: '100%',
    '&:hover': {
        borderColor: '#6366f1'
    },
    '&:focus': {
        borderColor: '#6366f1',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)'
    }
};

export default function RegrasClassificacaoModal({ open, onClose, onApplyRules, data = [], periodoFiltro, isContador }) {
    const [rules, setRules] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [categoriasByGrupo, setCategoriasByGrupo] = useState({});
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState('local');
    const [testText, setTestText] = useState('');
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        if (open) {
            loadRules();
            fetchGroups();
        }
    }, [open]);

    const loadRules = async () => {
        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("/contabil/lancamento/regras/listar", {});
            if (Array.isArray(res)) {
                const uniqueGroups = new Set();
                const rulesToLoad = res.map(r => {
                    const gId = r.categoria?.grupo?.id || r.grupo?.id;
                    if (gId) uniqueGroups.add(gId);
                    return {
                        ...r,
                        keyword: r.palavraChave || r.keyword,
                        matchType: r.tipoBusca || r.matchType || 'contains',
                        tipo: r.tipoLancamento || r.tipo || '',
                        grupoId: gId,
                        categoriaId: r.categoria?.id,
                        enabled: r.snAtivo === 'S' || r.enabled === true,
                        isGlobal: !r.cliente?.id && !r.clienteId?.id
                    };
                });
                setRules(rulesToLoad);
                setSelectedRowKeys(rulesToLoad.filter(r => r.enabled).map(r => r.id));
                uniqueGroups.forEach(id => fetchCategorias(id));
            }
        } catch (e) {
            message.error("Erro ao carregar regras.");
        } finally {
            setLoading(false);
        }
    };

    const fetchGroups = async () => {
        try {
            const res = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/grupo/listar", {});
            setGrupos(Array.isArray(res) ? res : []);
        } catch (error) { console.error(error); }
    };

    const fetchCategorias = async (grupoId) => {
        if (categoriasByGrupo[grupoId]) return;
        try {
            const res = await Method_POST_CONTEXTO("/contabil/lancamento/categoria/listar", {
                grupo: { id: grupoId },
                situacao: { id: "A" }
            });
            setCategoriasByGrupo(prev => ({ ...prev, [grupoId]: Array.isArray(res) ? res : [] }));
        } catch (error) { console.error(error); }
    };

    const handleAddRule = () => {
        const isAddingGlobal = activeTab === 'global' && isContador;
        
        const newRule = {
            id: Date.now(),
            keyword: '',
            matchType: 'contains',
            grupoId: null,
            categoriaId: null,
            tipo: '',
            enabled: true,
            isGlobal: isAddingGlobal,
            isNew: true // Marcador para garantir que seja editável
        };

        if (!isAddingGlobal && activeTab === 'global') {
            setActiveTab('local');
            message.info("Mudamos para 'Regras do Cliente' para você criar sua regra.");
        }

        setRules(prev => [newRule, ...prev]);
    };

    const handleUpdateRule = (ruleId, field, value) => {
        setRules(prev => prev.map(r => {
            if (r.id !== ruleId) return r;
            const updated = { ...r, [field]: value, isDirty: true };
            if (field === 'grupoId') {
                updated.categoriaId = null;
                if (value) fetchCategorias(value);
            }
            if (field === 'categoriaId' && updated.grupoId) {
                const cat = (categoriasByGrupo[updated.grupoId] || []).find(c => c.id === value);
                if (cat) updated.categoriaNome = cat.nome;
            }
            return updated;
        }));
    };

    const handleBulkDelete = () => {
        Modal.confirm({
            title: 'Confirmar Exclusão',
            content: `Deseja excluir as ${selectedRowKeys.length} regras selecionadas?`,
            okText: 'Sim, excluir',
            okType: 'danger',
            centered: true,
            onOk: () => {
                setRules(prev => prev.filter(r => !selectedRowKeys.includes(r.id)));
                setSelectedRowKeys([]);
                message.success("Regras removidas. Salve para confirmar.");
            }
        });
    };

    const handleSave = async () => {
        const payload = rules.filter(r => r.keyword && r.categoriaId).map(r => ({
            palavraChave: r.keyword,
            tipoBusca: r.matchType,
            tipoLancamento: r.tipo || null,
            snAtivo: r.enabled ? 'S' : 'N',
            categoria: { id: Number(r.categoriaId) },
            grupo: { id: Number(r.grupoId) },
            id: (r.id < 1700000000000) ? r.id : undefined,
            clienteId: r.isGlobal ? null : undefined
        }));
        setLoading(true);
        try {
            await Method_POST_CONTEXTO("/contabil/lancamento/regras/salvar", payload);
            message.success("Regras salvas com sucesso!");
            loadRules();
        } catch (e) { message.error("Erro ao salvar."); }
        finally { setLoading(false); }
    };

    const handleApplyNow = async () => {
        if (!periodoFiltro) return message.warning("Selecione um período.");
        Modal.confirm({
            title: 'Aplicar Automação',
            content: 'Deseja processar os lançamentos do período agora?',
            onOk: async () => {
                setApplying(true);
                try {
                    const pesquisa = {
                        dataInicio: periodoFiltro[0].startOf('day').toISOString(),
                        dataTermino: periodoFiltro[1].endOf('day').toISOString()
                    };
                    const total = await Method_POST_CONTEXTO("/contabil/lancamento/regras/aplicar-lote", pesquisa);
                    message.success(`${total || 0} lançamentos classificados!`);
                    if (onApplyRules) onApplyRules();
                } catch (e) { message.error("Erro ao aplicar."); }
                finally { setApplying(false); }
            }
        });
    };

    const getMatchCount = (rule) => {
        if (!rule.keyword) return 0;
        const kw = rule.keyword.toLowerCase();
        return data.filter(item => {
            const desc = (item.descricao || '').toLowerCase();
            if (rule.tipo && rule.tipo !== (item.tipo?.id || item.tipo)) return false;
            if (rule.matchType === 'startsWith') return desc.startsWith(kw);
            if (rule.matchType === 'exact') return desc === kw;
            return desc.includes(kw);
        }).length;
    };

    const filteredRules = useMemo(() => rules.filter(r => {
        const matchesTab = activeTab === 'global' ? r.isGlobal : !r.isGlobal;
        if (!matchesTab) return false;
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return r.keyword?.toLowerCase().includes(search) || r.categoriaNome?.toLowerCase().includes(search);
    }), [rules, activeTab, searchText]);

    const stats = useMemo(() => ({
        local: rules.filter(r => !r.isGlobal).length,
        global: rules.filter(r => r.isGlobal).length,
        active: rules.filter(r => r.enabled).length
    }), [rules]);

    const totalMatches = useMemo(() => {
        return rules.filter(r => selectedRowKeys.includes(r.id) && r.keyword && r.enabled)
            .reduce((sum, r) => sum + getMatchCount(r), 0);
    }, [rules, selectedRowKeys, data]);

    const testResult = useMemo(() => {
        if (!testText) return null;
        const text = testText.toLowerCase();
        return rules.filter(r => {
            if (!r.keyword || !r.enabled) return false;
            const kw = r.keyword.toLowerCase();
            if (r.matchType === 'startsWith') return text.startsWith(kw);
            if (r.matchType === 'exact') return text === kw;
            return text.includes(kw);
        });
    }, [testText, rules]);

    const columns = [
        {
            title: <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>PALAVRA-CHAVE</Text>,
            dataIndex: 'keyword',
            width: '20%',
            render: (text, record) => (
                <Input
                    placeholder="Ex: POSTO..."
                    value={text}
                    onChange={e => handleUpdateRule(record.id, 'keyword', e.target.value)}
                    variant="borderless"
                    style={{ 
                        ...inputStyle,
                        fontWeight: 600,
                        border: record.isNew ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                        color: record.isGlobal ? '#64748b' : '#1e293b'
                    }}
                    disabled={record.isGlobal && !isContador && !record.isNew}
                />
            )
        },
        {
            title: <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>BUSCA</Text>,
            dataIndex: 'matchType',
            width: '15%',
            render: (val, record) => (
                <Select
                    value={val}
                    onChange={v => handleUpdateRule(record.id, 'matchType', v)}
                    variant="borderless"
                    style={{ ...inputStyle, height: 44 }}
                    disabled={record.isGlobal && !isContador && !record.isNew}
                    options={[{ value: 'contains', label: 'Contém' }, { value: 'startsWith', label: 'Início' }, { value: 'exact', label: 'Exato' }]}
                />
            )
        },
        {
            title: <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>GRUPO</Text>,
            width: '25%',
            dataIndex: 'grupoId',
            render: (val, record) => (
                <Select
                    placeholder="Selecione o Grupo"
                    value={val}
                    onChange={v => handleUpdateRule(record.id, 'grupoId', v)}
                    style={{ ...inputStyle, height: 44 }}
                    variant="borderless"
                    disabled={record.isGlobal && !isContador && !record.isNew}
                    showSearch
                    optionFilterProp="children"
                >
                    {grupos.map(g => <Select.Option key={g.id} value={g.id}>{g.nome}</Select.Option>)}
                </Select>
            )
        },
        {
            title: <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: 800, letterSpacing: '1px' }}>CATEGORIA</Text>,
            width: '32%',
            dataIndex: 'categoriaId',
            render: (val, record) => (
                <Select
                    placeholder="Selecione a Categoria"
                    value={val}
                    onChange={v => handleUpdateRule(record.id, 'categoriaId', v)}
                    style={{ ...inputStyle, height: 44 }}
                    variant="borderless"
                    disabled={!record.grupoId || (record.isGlobal && !isContador && !record.isNew)}
                    showSearch
                    optionFilterProp="children"
                >
                    {(categoriasByGrupo[record.grupoId] || []).map(c => <Select.Option key={c.id} value={c.id}>{c.nome}</Select.Option>)}
                </Select>
            )
        },
        {
            title: '',
            width: '8%',
            align: 'right',
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Testar Regra">
                        <Button 
                            type="text" 
                            size="middle"
                            style={{ color: '#6366f1' }}
                            icon={<Play size={18} />} 
                            onClick={() => { setIsTesting(true); setTestText(record.keyword); }} 
                        />
                    </Tooltip>
                    {(!record.isGlobal || isContador) && (
                        <Popconfirm
                            title="Remover regra?"
                            onConfirm={() => {
                                setRules(prev => prev.filter(r => r.id !== record.id));
                                message.success("Removida.");
                            }}
                            okText="Sim"
                            cancelText="Não"
                        >
                            <Button 
                                type="text" 
                                size="middle"
                                danger 
                                icon={<Trash2 size={18} />} 
                            />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={isTesting ? 1200 : 1000}
            footer={null}
            style={{ top: 20 }}
            className="premium-modal-v2"
            destroyOnClose
            closable={true}
        >
            <div style={{ padding: '8px' }}>
                <header style={{
                    marginBottom: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20
                }}>
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>Regras de Classificação Inteligente</Title>
                        <Text type="secondary">Gerencie a automação contábil e a padronização dos lançamentos.</Text>
                    </div>
                </header>

                <div style={{ display: 'flex', gap: 24 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Stats */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                            <Card size="small" style={cardStyle}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Regras do Cliente</Text>
                                    <Title level={3} style={{ margin: 0, fontWeight: 800 }}>{stats.local}</Title>
                                </Space>
                                <User size={22} style={{ position: 'absolute', right: 16, top: 16, color: '#94a3b8' }} />
                            </Card>
                            <Card size="small" style={cardStyle}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Regras Globais</Text>
                                    <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#f59e0b' }}>{stats.global}</Title>
                                </Space>
                                <Globe size={22} style={{ position: 'absolute', right: 16, top: 16, color: '#f59e0b', opacity: 0.8 }} />
                            </Card>
                            <Card size="small" style={{ ...cardStyle, background: '#f0fdf4' }}>
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Regras Ativas</Text>
                                    <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#10b981' }}>{stats.active}</Title>
                                </Space>
                                <Activity size={22} style={{ position: 'absolute', right: 16, top: 16, color: '#10b981' }} />
                            </Card>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                            <Segmented 
                                size="large"
                                value={activeTab}
                                onChange={setActiveTab}
                                options={[
                                    { label: <Space><User size={14} /> Regras do Cliente</Space>, value: 'local' },
                                    { label: <Space><Globe size={14} /> Regras do Sistema</Space>, value: 'global' }
                                ]}
                                style={{ borderRadius: 12, padding: 4, background: '#f1f5f9' }}
                            />
                        </div>

                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            marginBottom: 24, 
                            padding: '12px 16px', 
                            background: '#ffffff', 
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                        }}>
                            <Input 
                                placeholder="Pesquisar palavra-chave..." 
                                prefix={<Search size={18} color="#94a3b8" />} 
                                value={searchText} 
                                onChange={e => setSearchText(e.target.value)} 
                                variant="borderless"
                                style={{ width: 400, fontSize: 15 }} 
                                allowClear 
                            />
                            <Space size={12}>
                                <AnimatePresence>
                                    {selectedRowKeys.length > 0 && (
                                        <motion.div initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 20, opacity: 0 }}>
                                            <Button 
                                                danger 
                                                type="primary" 
                                                icon={<Trash2 size={16} />} 
                                                onClick={handleBulkDelete}
                                                style={{ height: 40, borderRadius: 12, fontWeight: 600 }}
                                            >
                                                Excluir ({selectedRowKeys.length})
                                            </Button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                                <Button 
                                    icon={<TestTube size={18} />} 
                                    onClick={() => setIsTesting(!isTesting)} 
                                    style={{ height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, border: 'none', background: '#f1f5f9' }}
                                >
                                    {isTesting ? 'Fechar Sandbox' : 'Sandbox'}
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<Plus size={18} />} 
                                    onClick={handleAddRule} 
                                    style={{ height: 40, borderRadius: 12, background: '#6366f1', fontWeight: 700, border: 'none', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}
                                >
                                    Nova Regra
                                </Button>
                            </Space>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <Table
                                dataSource={filteredRules}
                                columns={columns}
                                rowKey="id"
                                pagination={false}
                                size="middle"
                                loading={loading}
                                scroll={{ y: 350 }}
                                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                                locale={{ emptyText: <Empty description="Nenhuma regra encontrada." /> }}
                                onRow={() => ({
                                    style: { cursor: 'pointer' }
                                })}
                            />
                        </div>
                    </div>

                    <AnimatePresence>
                        {isTesting && (
                            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 320, opacity: 1 }} exit={{ width: 0, opacity: 0 }} style={{ background: '#f8fafc', borderRadius: 20, padding: 20, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TestTube size={18} /> Sandbox</Title>
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>Teste como as descrições serão classificadas.</Text>
                                <Input.TextArea placeholder="Ex: NF 123 POSTO SHELL" rows={4} value={testText} onChange={e => setTestText(e.target.value)} style={{ borderRadius: 12, marginBottom: 20 }} />
                                <div style={{ minHeight: 150 }}>
                                    {testResult && testResult.length > 0 ? (
                                        testResult.map(r => (
                                            <div key={r.id} style={{ background: '#f0fdf4', border: '1px solid #10b981', padding: 12, borderRadius: 12, marginBottom: 10 }}>
                                                <Text strong style={{ display: 'block' }}>{r.keyword}</Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>{r.categoriaNome}</Text>
                                            </div>
                                        ))
                                    ) : testText ? <div style={{ textAlign: 'center', padding: 20, background: '#fff1f2', borderRadius: 12 }}><CloseCircleOutlined style={{ color: '#fb7185', fontSize: 24 }} /><br /><Text type="secondary">Nenhum match encontrado.</Text></div> : <div style={{ textAlign: 'center', padding: 20 }}><MousePointerClick style={{ color: '#cbd5e1' }} /><br /><Text type="secondary">Aguardando entrada...</Text></div>}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div style={{ marginTop: 20, padding: 12, background: '#f0fdfa', borderRadius: 12, border: '1px solid #ccfbf1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space><ShieldCheck size={18} color="#0d9488" /><Text style={{ fontSize: 12, color: '#115e59', fontWeight: 500 }}>Regras do Sistema garantem a padronização global do portal.</Text></Space>
                </div>

                {/* Footer Buttons no padrão premium-modal-v2 */}
                <div
                    className="footer-buttons"
                    style={{
                        marginTop: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        paddingTop: 24,
                        borderTop: '1px solid #f1f5f9',
                        flexWrap: 'wrap'
                    }}
                >
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={onClose}
                        size="large"
                        style={{ borderRadius: 10 }}
                    >
                        Voltar
                    </Button>
                    <Button
                        type="default"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={loading}
                        size="large"
                        style={{ borderRadius: 10, fontWeight: 600 }}
                    >
                        Salvar Regras
                    </Button>
                    <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        onClick={handleApplyNow}
                        loading={applying}
                        disabled={selectedRowKeys.length === 0}
                        size="large"
                        style={{ borderRadius: 10, background: '#10b981', border: 'none', fontWeight: 700 }}
                    >
                        Aplicar em Lote ({totalMatches})
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
